// ============================================================
// ANSICHTSSACHE-AGENT – Dialog/Modal Component
// Focus trap, smooth animation, backdrop blur, a11y
// ============================================================
// DAS EINE MODAL DES HAUSES. Vorbild fuer Gestalt und Mechanik ist
// das Ausgaben-Modal (`ausgaben/expense-dialog.tsx`, Ansage vom
// 24.09.2026). Jedes Modal baut sich aus den Teilen dieser Datei,
// und zwar aus allen, die es braucht — keine eigenen Abschriften
// von Kopf, Fuss, Knopfreihe oder Speicherlogik. Nur so wirkt eine
// Aenderung hier in JEDES Modal durch, statt in sechzig Dateien
// nachgezogen zu werden:
//
//   Gestalt   Dialog (size), DialogHeader/Title/Description,
//             DialogContent, DialogColumns/Column, DialogRow,
//             DialogSection, DialogNotice, DialogTotals,
//             DialogFooter mit DialogFooterStart
//   Knoepfe   DialogCancel, DialogSubmit, DialogSideAction
//   Mechanik  DialogForm — absenden, waehrenddessen sperren,
//             Fehler melden, schliessen, Seite auffrischen
//
// Die Beschriftung der Felder kommt aus `FieldLabel`; wie sie im
// Modal aussieht (Versalien), steht unten am Kasten.
// ============================================================

"use client";

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  useTransition,
} from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { useLiveCover } from "@/components/live";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "./button";
import { FieldLabel } from "./field-label";
import { toast } from "./toast";
import { errorOf } from "@/lib/action-result";

/**
 * Was die Teile im Inneren vom Dialog wissen muessen: wie er
 * schliesst. `close` ist das Ende nach getaner Arbeit, `cancel`
 * das Abbrechen (Schliessen-Kreuz, Escape, Klick daneben,
 * „Abbrechen“) — beim Ausgaben-Modal wirft es einen eben
 * hochgeladenen Beleg wieder weg, nach dem Einreichen darf es
 * das gerade nicht.
 */
const DialogContext = createContext<{
  close: () => void;
  cancel: () => void;
  /** Ruft `DialogForm`, solange es speichert — dann schliesst Abbrechen nicht. */
  setBusy: (busy: boolean) => void;
} | null>(null);

/** Ob das Formular des Dialogs gerade speichert. */
const DialogPendingContext = createContext(false);

/**
 * Fuer Felder und Knoepfe im Dialog, die sich waehrend des
 * Speicherns sperren sollen (`disabled={pending}`).
 */
function useDialogPending() {
  return useContext(DialogPendingContext);
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Was beim ABBRECHEN geschieht — Kreuz, Escape, Klick daneben
   * und `DialogCancel`. Ohne Angabe ist es `onClose`. Gesetzt ist
   * es der Weg, auf dem etwas Halbfertiges wieder verschwindet
   * (ein abgelegter Beleg); die Funktion schliesst dann selbst.
   * Nach erfolgreichem Absenden ruft `DialogForm` nur `onClose`.
   */
  onCancel?: () => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Die Breite auf dem Desktop.
   *
   * `default` ist der schmale Dialog fuer eine Handvoll Felder
   * (512px). `wide` ist fuer LANGE Formulare: ab 1024px
   * Bildschirmbreite geht er auf bis 1024px auf, und der Inhalt
   * steht in zwei Spalten (`DialogColumns`). Ein langer Dialog in 512px
   * Breite war auf einem 27-Zoll-Bildschirm ein Schacht, durch den
   * man scrollt, waehrend links und rechts zwei Drittel des Bildes
   * leer standen. Darunter bleibt er einspaltig und schmal — ein
   * einspaltiges Formular in 1000px Breite zieht jedes Feld auf
   * Zeilenlaenge.
   */
  size?: "default" | "wide";
  /**
   * Ob der Fokus beim Oeffnen ins erste Feld springt. Richtig fuer
   * alles, was angelegt oder beantwortet wird. Falsch fuer einen
   * Dialog, der Angaben ZEIGT und sie an Ort und Stelle aendern
   * laesst (Stammdaten einer Person): dort stuende sonst das erste
   * stille Feld beim Oeffnen schon offen, mit Kante und Ring, als
   * wolle man gerade den Vornamen aendern. Der Fokus geht dann auf
   * den Dialog selbst — Tab und Escape wirken trotzdem darin.
   */
  focusFirstField?: boolean;
}

function Dialog({
  open,
  onClose,
  onCancel,
  children,
  className,
  size = "default",
  focusFirstField = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Solange `DialogForm` speichert, schliessen Kreuz, Escape und
  // Klick daneben nicht: der Dialog verschwaende, waehrend die
  // Aktion noch laeuft, und ihre Fehlermeldung stuende ohne das
  // Formular da, um das es geht.
  const busy = useRef(false);
  const cancel = () => {
    if (busy.current) return;
    (onCancel ?? onClose)();
  };

  /**
   * Die Schliessen-Funktion liegt in einem Ref.
   *
   * SONST SPRINGT DER FOKUS BEI JEDEM TASTENDRUCK. Fast jeder
   * Aufruf uebergibt `onClose={() => setOffen(false)}` — eine
   * frische Funktion je Durchgang. Stuende sie in der
   * Abhaengigkeitsliste des Effekts darunter, liefe der bei
   * jedem Rendern neu; und weil er den Fokus auf das ERSTE Feld
   * setzt, landete man nach jedem getippten Zeichen wieder in
   * der Bezeichnung. In Dialogen mit unkontrollierten Feldern
   * fiel das nie auf — die rendern beim Tippen nicht neu.
   *
   * Das Ref wird im Effekt gesetzt und nicht beim Rendern:
   * `react-hooks/refs` meldet sonst jeden Zugriff.
   */
  const schliessen = useRef(cancel);
  useEffect(() => {
    schliessen.current = cancel;
  });

  useEffect(() => {
    if (!open) return undefined;

    function handleEscape(e: KeyboardEvent) {
      // Ein Escape, das schon jemand genommen hat (ein Feld, das
      // seinen Entwurf verwirft; der Ticket-Reporter, der über dem Dialog
      // liegt), schließt nicht auch noch den Dialog. Radix hält
      // es beim Seitenblatt genauso.
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        schliessen.current();
      }
    }

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    // Der Fokus wandert EINMAL beim Oeffnen auf das erste Feld.
    // `select:not([tabindex="-1"])`: das Auswahlfeld haelt ein
    // verdecktes `select` fuer die `FormData` bereit — der
    // Fokus darf nicht darin landen, sichtbar ist der Knopf.
    const bild = requestAnimationFrame(() => {
      if (!focusFirstField) {
        dialogRef.current?.focus();
        return;
      }
      const firstInput = dialogRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select:not([tabindex="-1"]), textarea, button[type="submit"]',
      );
      firstInput?.focus();
    });

    return () => {
      cancelAnimationFrame(bild);
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, focusFirstField]);

  // Focus trap
  const handleTabKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input:not([type="hidden"]), select:not([tabindex="-1"]), textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onKeyDown={handleTabKey}
    >
      {/* Backdrop with blur */}
      <div
        className="animate-fade-in fixed inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={cancel}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        ref={dialogRef}
        // Nur per Skript fokussierbar (`focusFirstField={false}`),
        // nie ueber die Tabulatortaste.
        tabIndex={-1}
        data-dialog=""
        className={cn(
          "bg-bg-primary shadow-modal relative w-full max-w-lg rounded-lg outline-none",
          size === "wide" && "lg:max-w-5xl",
          "animate-slide-up",
          // KOPF UND FUSS STEHEN, NUR DER RUMPF ROLLT (seit
          // 24.09.2026, auf Ansage). Der Kasten ist eine Spalte:
          // `DialogHeader` und `DialogFooter` behalten ihre Hoehe,
          // `DialogContent` nimmt den Rest und rollt in sich. Bis
          // dahin rollte der ganze Kasten, und bei einem langen
          // Formular verschwand „Speichern“ unter dem Rand.
          // `overflow-y-auto` bleibt als Rueckfall: packt ein Modal
          // Kopf, Rumpf und Fuss in eine eigene Huelle ohne diese
          // Spalte, rollt es wie vorher als Ganzes.
          // `dvh` statt `vh`: Safari auf dem iPhone rechnet `vh` mit
          // eingeklappter Adressleiste — bei sichtbarer Leiste lag der
          // Fuss mit „Speichern“ unter ihr.
          "flex max-h-[min(90dvh,calc(100dvh-2rem))] flex-col overflow-y-auto",
          // Der Dialog steht im Baum dort, wo sein Ausloeser steht —
          // er wird nicht in den Seitenrumpf umgehaengt. Damit erbt
          // er auch dessen Textregeln, und eine einzeilige Umgebung
          // (der Vorspann des Seitenkopfes, eine Tabellenzeile)
          // vererbt `white-space: nowrap`: der Beschreibungssatz
          // lief dann als EINE Zeile aus dem Kasten heraus, 500
          // Pixel weit. Ein Dialog setzt seine eigenen Textregeln.
          "text-left whitespace-normal",
          // Feldbeschriftungen im Modal in Versalien, wie im
          // Ausgaben-Modal. Die EINE Stelle dafuer: im Quelltext
          // stehen Beschriftungen normal geschrieben, `FieldLabel`
          // traegt `data-field-label`, und hier wird entschieden,
          // wie sie im Modal aussehen.
          "[&_[data-field-label]]:uppercase",
          className,
        )}
      >
        {/* Das Schliessen-Kreuz. `z-20`, nicht `z-10`: der Kopf
            (`DialogHeader`) ist seit seinem Rollschatten selbst
            `relative z-10` und steht im Baum NACH dem Kreuz — bei
            gleicher Stufe lag er darueber, das Kreuz war zu sehen,
            aber jeder Klick landete im Kopf (gemeldet 25.09.2026 an
            den Stammdaten, betraf jedes Modal mit Kopf). */}
        <button
          type="button"
          onClick={cancel}
          className="text-text-tertiary hover:text-text-primary hover:bg-bg-secondary absolute top-4 right-4 z-20 flex h-8 w-8 items-center justify-center rounded transition-colors"
          aria-label="Schließen"
        >
          ✕
        </button>
        <DialogContext.Provider
          value={{
            close: onClose,
            cancel,
            setBusy: (wert) => {
              busy.current = wert;
            },
          }}
        >
          {children}
        </DialogContext.Provider>
      </div>
    </div>
  );
}

/**
 * Der Kopf. Er rollt nicht mit — der Titel bleibt stehen.
 *
 * Sobald der Rumpf gerollt ist (`data-scrolled` an
 * `DialogContent`), wirft er einen weichen Schatten nach unten:
 * der Inhalt taucht sichtbar UNTER dem Kopf durch, statt kantenlos
 * am Titel abgeschnitten zu werden. `relative z-10`, damit der
 * Schatten ueber dem Rumpf liegt und nicht von ihm verdeckt wird.
 * Das Schliessen-Kreuz muss darum hoeher stehen (`z-20` in
 * `Dialog`) — sonst faengt der Kopf seine Klicks ab.
 *
 * Der Abstand zwischen Titel und erstem Feld steht deshalb zum
 * groessten Teil HIER (`pb-3`) und nicht im Rumpf: sonst saesse
 * der Schatten direkt an der Unterkante des Titels. Der Rumpf
 * behaelt oben 4 px — genug fuer den Fokusring des ersten Felds,
 * den sein Rollbereich sonst abschnitte.
 */
function DialogHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      data-dialog-header=""
      className={cn(
        "relative z-10 shrink-0 px-6 pt-6 pr-12 pb-3",
        "duration-fast transition-shadow",
        "[[data-dialog]:has([data-scrolled])_&]:shadow-[0_10px_14px_-10px_rgb(0_0_0/0.18)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function DialogTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={cn("text-heading-md", className)}>{children}</h3>;
}

function DialogDescription({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <p className={cn("text-body-sm text-text-secondary mt-1", className)}>{children}</p>;
}

/**
 * Der Rumpf. Zwischen seinen Teilen steht immer derselbe Abstand
 * (`space-y-4`, wie im Ausgaben-Modal) — ein Modal regelt ihn
 * nicht selbst mit `mt-…` an einzelnen Feldern.
 *
 * Er ist der Teil, der rollt, wenn das Formular laenger ist als
 * der Bildschirm; Kopf und Fuss bleiben dabei stehen.
 */
function DialogContent({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      // Sobald gerollt ist, wirft der Kopf einen Schatten
      // (`DialogHeader`) — ohne ihn verschwaende der Inhalt
      // kantenlos unter dem Titel. Das Merkmal wird direkt am
      // Element gesetzt, nicht ueber Zustand: Rollen soll nichts
      // neu zeichnen.
      onScroll={(e) =>
        e.currentTarget.toggleAttribute("data-scrolled", e.currentTarget.scrollTop > 0)
      }
      // `relative`, damit absolut gesetzte Kinder (`sr-only`-Felder,
      // das verdeckte Dateifeld der Unterschrift) im Rollbereich
      // bleiben. Ohne ihn verlaengerten sie die Huelle, und der
      // ganze Dialog samt Fuss rollte mit (01.10.2026).
      className={cn(
        "relative min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4",
        // Mit Kopf traegt der den Abstand nach oben (`DialogHeader`).
        "[[data-dialog]:has([data-dialog-header])_&]:pt-1",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Zwei Spalten fuer einen langen Dialog (`size="wide"`).
 *
 * Jede Spalte ist eine `DialogColumn` und traegt eine SACHE — links
 * etwa, was auf dem Beleg steht, rechts, wie er gebucht wird. Eine
 * Spalte, die nur die zweite Haelfte der Feldliste aufnimmt, liest
 * sich wie ein umbrochener Text. Die Reihenfolge im Quelltext ist
 * die Lesereihenfolge: auf dem Telefon und im schmalen Fenster
 * stehen die Spalten untereinander, und die Tabulatortaste geht die
 * linke erst ganz hinunter, bevor sie rechts weitermacht.
 *
 * Was beide Spalten braucht (eine Positionstabelle, eine Vorschau),
 * steht ausserhalb, direkt im `DialogContent` darunter oder darueber.
 */
function DialogColumns({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-2", className)}>
      {children}
    </div>
  );
}

function DialogColumn({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("min-w-0 space-y-4", className)}>{children}</div>;
}

/**
 * Felder nebeneinander in EINER Zeile: Rechnungssteller neben
 * Rechnungsnummer, Datum neben Betrag. Zusammen steht, was man in
 * einem Zug liest — nicht zwei beliebige Felder, weil Platz ist.
 *
 * Auf dem Telefon stehen sie untereinander. Ungleiche Breiten
 * („Firmenname“ breit, „Kürzel“ schmal) gibt `template` als
 * Spaltenvorlage an („1fr 7rem“) — nicht `grid-cols-[…]` in
 * `className`, das schaltete das Stapeln ab.
 */
function DialogRow({
  children,
  columns = 2,
  template,
  className,
}: {
  children: React.ReactNode;
  columns?: 2 | 3;
  template?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4",
        template
          ? "sm:grid-cols-[var(--dialog-row)]"
          : columns === 3
            ? "sm:grid-cols-3"
            : "sm:grid-cols-2",
        className,
      )}
      style={template ? ({ "--dialog-row": template } as React.CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Eine Wahl unter wenigen Moeglichkeiten, die man erst verstehen
 * muss: jede als Karte mit Punkt, Namen und einem Satz darunter
 * („Rechnung aus Zeiten“: wie die Zeiten auf dem Beleg stehen;
 * „Deaktivieren“: pausiert oder ausgeschieden). Fuer eine blosse
 * Liste von Werten ist `Select` da.
 *
 * Mit `name` traegt ein verborgenes Feld die Wahl in die
 * `FormData` — `DialogForm` bekommt sie dann wie jedes andere
 * Feld.
 */
function DialogChoice<T extends string>({
  label,
  name,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  name?: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string; hint?: string }[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <FieldLabel>{label}</FieldLabel>
      {name && <input type="hidden" name={name} value={value} />}
      <div role="radiogroup" aria-label={label} className="space-y-1">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                "duration-fast flex w-full items-start gap-3 rounded-lg border px-3 py-2 text-left transition-colors",
                active
                  ? "border-accent bg-accent-subtle/40"
                  : "border-border hover:bg-bg-secondary",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0 rounded-full border",
                  active ? "border-accent border-[5px]" : "border-control",
                )}
              />
              <span className="min-w-0">
                <span className="text-body-sm text-text-primary block font-medium">
                  {option.label}
                </span>
                {option.hint && (
                  <span className="text-caption text-text-tertiary block">{option.hint}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Ein abgesetzter Abschnitt: Haarlinie darueber, dann sein Inhalt.
 * Typisch mit einem Kaestchen als Schalter vorn (KSK-pflichtig,
 * Bewirtungsbeleg, Mehrere Positionen) und den Feldern, die es
 * aufklappt, darunter — was selten gebraucht wird, steht erst auf
 * Ansage da.
 */
function DialogSection({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("border-border-subtle space-y-3 border-t pt-4", className)}>{children}</div>
  );
}

const NOTICE_TONES = {
  neutral: "border-border bg-bg-secondary",
  accent: "border-accent/25 bg-accent-subtle",
  success: "border-success/25 bg-success-subtle",
  warning: "border-warning/25 bg-warning-subtle",
  danger: "border-danger/25 bg-danger-subtle",
} as const;

/**
 * Ein Kasten mit einer Auskunft ueber dem Formular: der Beleg, um
 * den es geht (neutral), ein Vorschlag, den man uebernehmen kann
 * (accent), eine gelungene Probe (success), „diese Rechnung ist
 * wohl schon erfasst“ (warning), ein Fehler, der nicht an einem
 * Feld haengt (danger). `action` steht rechts daneben, ein Knopf
 * fuer den Weg daraus.
 *
 * Das Zeichen steht oben an der ersten Zeile, damit es bei einem
 * mehrzeiligen Hinweis nicht in die Mitte rutscht; der Knopf
 * rechts bleibt mittig.
 */
function DialogNotice({
  tone = "neutral",
  icon,
  action,
  children,
  className,
}: {
  tone?: keyof typeof NOTICE_TONES;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-lg border px-3 py-2",
        NOTICE_TONES[tone],
        className,
      )}
    >
      {icon && <div className="flex h-[18px] shrink-0 items-center self-start">{icon}</div>}
      <div className="text-body-sm text-text-primary min-w-0 flex-1">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/**
 * Die Summenzeile unter dem Formular — „Netto … · USt … · Brutto
 * …“. Sie steht da, damit man mit dem Beleg vergleichen kann,
 * ohne zu rechnen; der Posten, auf den es ankommt, ist `strong`.
 */
function DialogTotals({
  items,
  className,
}: {
  items: { label?: string; value: React.ReactNode; strong?: boolean }[];
  className?: string;
}) {
  return (
    <p className={cn("text-body-sm text-text-secondary numeric tabular-nums", className)}>
      {items.map((item, index) => (
        <Fragment key={item.label ?? index}>
          {index > 0 && " · "}
          <span className={item.strong ? "text-text-primary font-medium" : undefined}>
            {item.label ? `${item.label} ` : ""}
            {item.value}
          </span>
        </Fragment>
      ))}
    </p>
  );
}

function DialogFooter({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        // `shrink-0`: der Fuss rollt nicht mit, „Speichern“ bleibt
        // immer im Blick.
        "border-border mt-2 flex shrink-0 items-center justify-end gap-3 border-t px-6 pt-2 pb-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Die linke Seite des Fusses: Loeschen, Archivieren und was sonst
 * NICHT mit dem Absenden konkurriert. Rechts stehen immer nur
 * `DialogCancel` und `DialogSubmit`.
 */
function DialogFooterStart({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("mr-auto flex items-center gap-1", className)}>{children}</div>;
}

/**
 * Abbrechen. Schliesst ueber denselben Weg wie Kreuz und Escape
 * (`onCancel` am Dialog, sonst `onClose`) und ist gesperrt,
 * solange gespeichert wird.
 */
function DialogCancel({
  children = "Abbrechen",
  onClick,
  pending,
  disabled,
  ...props
}: Omit<ButtonProps, "type" | "variant"> & { pending?: boolean }) {
  const dialog = useContext(DialogContext);
  const formPending = useDialogPending();
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled || pending || formPending}
      onClick={onClick ?? dialog?.cancel}
      {...props}
    >
      {children}
    </Button>
  );
}

/**
 * Der Knopf, der die Sache tut. Seine Beschriftung ist das Verb
 * („Einreichen“, „Projekt anlegen“, „Zahlung buchen“, beim
 * Aendern „Speichern“) — nie „OK“. Waehrend `DialogForm`
 * speichert, dreht er sich und ist gesperrt; ein eigenes
 * `pending` (Knopf mit eigener Transition) kommt hinzu.
 */
function DialogSubmit({
  children,
  pending,
  type = "submit",
  ref,
  ...props
}: Omit<ButtonProps, "loading"> & { pending?: boolean; ref?: React.Ref<HTMLButtonElement> }) {
  const formPending = useDialogPending();
  return (
    <Button ref={ref} type={type} loading={Boolean(pending) || formPending} {...props}>
      {children}
    </Button>
  );
}

/**
 * Ein Knopf fuer die linke Seite des Fusses. `tone="danger"`
 * faerbt ihn rot (Loeschen) — die Rueckfrage davor stellt der
 * Aufrufer mit `confirmDialog`.
 */
function DialogSideAction({
  tone,
  className,
  pending,
  disabled,
  ...props
}: Omit<ButtonProps, "type" | "variant"> & { tone?: "danger"; pending?: boolean }) {
  const formPending = useDialogPending();
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled || pending || formPending}
      className={cn(tone === "danger" && "text-danger hover:text-danger", className)}
      {...props}
    />
  );
}

/**
 * `redirect()` in einer Aktion kommt im Browser als geworfener
 * Fehler an — ist aber der Erfolg: der Router wechselt schon aufs
 * Ziel (neuer Brief, neues Portal).
 */
function isRedirect(error: unknown): boolean {
  const digest = (error as { digest?: unknown } | null)?.digest;
  return typeof digest === "string" && digest.startsWith("NEXT_REDIRECT");
}

/** Die Meldung nach getaner Arbeit — eine Zeile, oder mit einer zweiten darunter. */
type DialogSuccess = string | { title: string; description?: string };

function announce(success: DialogSuccess | null | undefined) {
  if (!success) return;
  if (typeof success === "string") toast.success(success);
  else toast.success(success.title, { description: success.description });
}

// Was eine Aktion als Fehlschlag meldet, entscheidet `errorOf` aus
// `lib/action-result.ts` — EINE Auswertung fuer Dialog und `useAction`,
// die auch `fehler` und `ok: false` kennt.

/**
 * DIE MECHANIK EINES MODALS, an einer Stelle. So arbeitet das
 * Ausgaben-Modal, und so arbeitet jedes:
 *
 *   1. Absenden sammelt die `FormData` und ruft `action`.
 *   2. Waehrenddessen dreht sich `DialogSubmit`, `DialogCancel`
 *      und `DialogSideAction` sind gesperrt, ein zweites Absenden
 *      (Eingabetaste) geht ins Leere.
 *   3. Antwortet die Aktion mit `{ error }`, steht der Satz als
 *      Toast da und der Dialog bleibt offen — nichts Getipptes geht
 *      verloren. Wer den Fehler an ein Feld schreiben will, gibt
 *      `onError`.
 *   4. Sonst: `success` als Toast, Dialog zu (`onClose`, NICHT
 *      `onCancel`), dann `onSuccess` (etwa zum neuen Blatt
 *      springen) und die Seite auffrischen. Erst zu, dann weiter:
 *      ein Dialog, der beim Schliessen seine Adresse aufraeumt,
 *      raeumte sonst den Sprung gleich wieder weg.
 *
 * Eine Aktion, die wirft, landet in derselben Meldung statt in
 * einer roten Fehlerseite. Eine Aktion, die mit `redirect()` endet,
 * gilt als Erfolg: Meldung (nur als Text), Dialog zu, der Router
 * uebernimmt.
 */
function DialogForm<R>({
  action,
  success,
  onSuccess,
  onError,
  keepOpen = false,
  refresh = true,
  className,
  id,
  ref,
  children,
}: {
  action: (formData: FormData) => Promise<R> | R;
  /** Die Meldung nach getaner Arbeit — oder keine. */
  success?: DialogSuccess | ((result: R) => DialogSuccess | null | undefined);
  onSuccess?: (result: R) => void | Promise<void>;
  /** Statt des Toasts: der Aufrufer zeigt den Fehler selbst (am Feld). */
  onError?: (message: string, result: R | undefined) => void;
  /** Offen bleiben nach dem Absenden (Serienanlage). */
  keepOpen?: boolean;
  /** Die Seite dahinter auffrischen. Aus nur, wenn `onSuccess` ohnehin woandershin geht. */
  refresh?: boolean;
  className?: string;
  id?: string;
  ref?: React.Ref<HTMLFormElement>;
  children: React.ReactNode;
}) {
  const dialog = useContext(DialogContext);
  const router = useRouter();
  const coverLive = useLiveCover();
  const [pending, startTransition] = useTransition();
  // Der Zustand aus `useTransition` kommt erst mit dem naechsten
  // Zeichnen an; zwei Eingabetasten im selben Augenblick saehen
  // ihn beide noch auf `false`.
  const inFlight = useRef(false);

  function report(message: string, result: R | undefined) {
    if (onError) onError(message, result);
    else toast.error(message);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Ein Formular im Formular (Portal, verschachtelter Dialog)
    // schickt sein Absenden sonst auch an dieses hier.
    e.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    dialog?.setBusy(true);
    const formData = new FormData(e.currentTarget);
    startTransition(async () => {
      try {
        let result: R;
        try {
          result = await action(formData);
        } catch (err) {
          if (isRedirect(err)) {
            if (typeof success !== "function") announce(success);
            if (!keepOpen) dialog?.close();
            return;
          }
          console.error(err);
          report("Das hat nicht geklappt — bitte noch einmal versuchen.", undefined);
          return;
        }
        const error = errorOf(result);
        if (error) {
          report(error, result);
          return;
        }
        announce(typeof success === "function" ? success(result) : success);
        if (!keepOpen) dialog?.close();
        await onSuccess?.(result);
        if (refresh) {
          router.refresh();
          // Die Live-Auffrischung fuer dieses eigene Schreiben ist
          // damit erledigt — sonst zeichnet die Seite zweimal.
          coverLive();
        }
      } finally {
        inFlight.current = false;
        dialog?.setBusy(false);
      }
    });
  }

  return (
    <DialogPendingContext.Provider value={pending}>
      {/* Das Formular setzt die Spalte des Kastens fort — sonst
          waere es ein Block, und Kopf und Fuss rollten wieder mit. */}
      <form
        ref={ref}
        id={id}
        onSubmit={handleSubmit}
        className={cn("flex min-h-0 flex-1 flex-col", className)}
      >
        {children}
      </form>
    </DialogPendingContext.Provider>
  );
}

const nieAendern = () => () => {};

/**
 * Haengt einen Dialog an den Rumpf (`body`).
 *
 * Der Dialog selbst hat kein Portal: er steht, wo er im Baum steht.
 * Steht sein Knopf in einer Flaeche mit eigenem Stapel — eine
 * klebende Spalte (`sticky`), eine Zeile mit `z-[1]` —, liegt der
 * offene Dialog mit seiner Abdunklung UNTER allem, was danach kommt
 * (13.10.2026: „Neue Notiz“ aus der Notizspalte lag hinter dem
 * Blatt). Erst nach dem Laden im Browser, damit Server und erstes
 * Zeichnen uebereinstimmen.
 */
function DialogAmRumpf({ children }: { children: React.ReactNode }) {
  const imBrowser = useSyncExternalStore(
    nieAendern,
    () => true,
    () => false,
  );
  return imBrowser ? createPortal(children, document.body) : null;
}

export {
  Dialog,
  DialogAmRumpf,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogColumns,
  DialogColumn,
  DialogRow,
  DialogChoice,
  DialogSection,
  DialogNotice,
  DialogTotals,
  DialogFooter,
  DialogFooterStart,
  DialogCancel,
  DialogSubmit,
  DialogSideAction,
  DialogForm,
  useDialogPending,
};
